import {refreshTokenService} from "@/services/auth.service";
import {useAuthStore} from "@/store/authStore";
import {manageCatchError} from "./http.error";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

const defaultHeaders = {
  "Content-Type": "application/json",
};

let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

const processQueue = (token: string) => {
  for (const resolve of refreshQueue) resolve(token);
  refreshQueue = [];
};

const attemptTokenRefresh = async (): Promise<string> => {
  const {refreshToken, login, logout, user} = useAuthStore.getState();

  if (!refreshToken) {
    logout();
    throw new Error("sessionExpired");
  }

  if (isRefreshing) {
    return new Promise<string>(resolve => {
      refreshQueue.push(resolve);
    });
  }

  isRefreshing = true;

  try {
    const {data} = await refreshTokenService(refreshToken);
    if (!data) throw new Error("sessionExpired");

    const {toAuthSession} = await import("@/adapters/auth.adapter");
    const session = toAuthSession(data);
    login(session.accessToken, user ?? session.user, session.refreshToken);
    processQueue(session.accessToken);
    return session.accessToken;
  } catch {
    logout();
    throw new Error("sessionExpired");
  } finally {
    isRefreshing = false;
  }
};

export const buildQueryUrl = (base: string, params?: Record<string, unknown>): string => {
  if (!params) return base;
  const qs = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)])
  ).toString();
  return qs ? `${base}?${qs}` : base;
};

export const fetchInstance = async (
  method: HttpMethod,
  url: string,
  body?: unknown,
  additionalHeaders?: object,
  signal?: AbortSignal,
  isPublic?: boolean
) => {
  const token = useAuthStore.getState().accessToken;
  const tokenFinal = isPublic ? null : token;

  const headers = {
    ...defaultHeaders,
    ...(tokenFinal ? {Authorization: `Bearer ${token}`} : {}),
    ...additionalHeaders,
  };

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const responseJson = await response.json();

  if (!response.ok) {
    const status = response.status;

    if (status === 401 && !isPublic) {
      const newToken = await attemptTokenRefresh();
      const retryHeaders = {
        ...defaultHeaders,
        Authorization: `Bearer ${newToken}`,
        ...(additionalHeaders ?? {}),
      };
      const retryResponse = await fetch(url, {
        method,
        headers: retryHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal,
      });
      const retryJson = await retryResponse.json();
      if (!retryResponse.ok) {
        manageCatchError({
          status: retryResponse.status,
          message: retryJson?.message ?? retryJson?.error ?? retryResponse.statusText,
        });
      }
      return {
        data: retryJson.data ?? retryJson,
        status: retryResponse.status,
        message: retryJson.message,
        pagination: retryJson.pagination,
      };
    }

    if (status === 403 || status >= 500) {
      return manageCatchError({
        status,
        message: responseJson.message ?? responseJson?.error,
      });
    }
    const message = responseJson?.message || responseJson?.error || response?.statusText || "internalServerError";

    throw new Error(message);
  }

  return {
    data: responseJson.data ?? responseJson,
    status: response.status,
    message: responseJson.message,
    pagination: responseJson.pagination,
  };
};
